// import error messages
import ERROR_MESSAGES from './error-messages';

import { apiBaseUrl, postData, getData } from './common';

// import axios
const axios = require('axios').default;

const schoolBaseUrl = `${apiBaseUrl}school/`;

/**
 * Sends a verification code to the phone number entered on the registration form.
 * The code is later submitted as part of the registration payload.
 */
export const sendRegistrationOtp = async (phoneNumber) => {
  // build url
  const url = `${schoolBaseUrl}send-registration-otp`;

  // initiate request
  try {
    let response = await axios.post(url, { phoneNumber });
    return { success: true, message: response.data.message };
  } catch (error) {
    if (error.response) {
      // rate limited - note this endpoint answers with { success, message }
      if (error.response.status === 429) {
        return {
          success: false,
          message:
            error.response.data?.message ??
            'Too many attempts. Please wait a few minutes and try again.',
        };
      }

      if (error.response.status === 400) {
        return {
          success: false,
          message:
            error.response.data?.message ??
            ERROR_MESSAGES.REQUIRED_INFO_MISSING,
        };
      }

      return { success: false, message: ERROR_MESSAGES.UNKNOWN_SERVER_ERROR };
    }

    // could not contact server or could not receive a response
    return { success: false, message: ERROR_MESSAGES.UNKNOWN_ERROR };
  }
};

export const registerSchool = async (data) => {
  // build url
  const url = `${schoolBaseUrl}register`;

  // initiate request
  try {
    let response = await axios.post(url, data);
    return {
      success: true,
      user: response.data.user,
      message: response.data.message,
    };
  } catch (error) {
    if (error.response) {
      if (error.response.status === 409) {
        return { success: false, message: error.response.data.message };
      }

      if (error.response.status === 400) {
        // the API explains why on this status - a rejected or expired
        // verification code lands here, so the message has to reach the user
        return {
          success: false,
          message:
            error.response.data?.message ??
            ERROR_MESSAGES.REQUIRED_INFO_MISSING,
        };
      }

      if (error.response.status === 429) {
        return {
          success: false,
          message:
            error.response.data?.message ??
            'Too many attempts. Please wait a few minutes and try again.',
        };
      }

      return { success: false, message: ERROR_MESSAGES.UNKNOWN_SERVER_ERROR };
    }

    // could not contact server or could not receive a response
    return { success: false, message: ERROR_MESSAGES.UNKNOWN_ERROR };
  }
};

export const confirmActivation = async (email, code, password, pathName) => {
  // build url
  const url =
    pathName === '/account/reset-password/'
      ? apiBaseUrl + 'account/confirm-password-reset'
      : schoolBaseUrl + 'confirm-activation';

  // create request data
  const requestData = { email, code, password };

  // initiate request
  try {
    let response = await axios.post(url, requestData);
    return {
      success: response.data.success,
      message: response.data.message,
    };
  } catch (error) {
    if (error.response) {
      if (error.response.status === 400) {
        return {
          success: false,
          message: ERROR_MESSAGES.REQUIRED_INFO_MISSING,
        };
      }

      return { success: false, message: ERROR_MESSAGES.UNKNOWN_SERVER_ERROR };
    }

    // could not contact server or could not receive a response
    return { success: false, message: ERROR_MESSAGES.UNKNOWN_ERROR };
  }
};

export const updateProfileInfo = async (authToken, data) => {
  const response = await postData('school/update-profile', data, authToken);

  if (response.success) {
    return {
      success: true,
      user: response.data.user,
    };
  }

  return response;
};

export const updateRegistrarInfo = async (authToken, data) => {
  const response = await postData(
    'school/update-registrar-info',
    data,
    authToken,
  );

  if (response.success) {
    return {
      success: true,
      user: response.data.user,
    };
  }

  return response;
};

export const getUsers = async (authToken) => {
  const response = await getData('school/users', authToken);

  if (response.success) {
    return { success: true, users: response.data.users };
  }

  return response;
};

export const createNewUser = async (authToken, data) => {
  const response = await postData('account/new-user', data, authToken);

  if (response.success) {
    return {
      success: true,
      user: response.data.user,
    };
  }

  return response;
};

export const getDepartments = async (authToken) => {
  const response = await getData('school/departments', authToken);

  if (response.success) {
    return { success: true, departments: response.data.departments };
  }

  return response;
};

export const createNewDepartment = async (authToken, data) => {
  const response = await postData('school/department/new', data, authToken);

  if (response.success) {
    return {
      success: true,
      department: response.data.department,
    };
  }

  return response;
};

export const deleteDepartment = async (authToken, data) => {
  return await postData('school/department/delete', data, authToken);
};

export const uploadSchoolLogo = async (authToken, data) => {
  const response = await postData('school/upload-photo', data, authToken);

  if (response.success)
    return { success: true, student: response.data.student };

  return response;
};
